import { digestsApi } from '@/lib/api';
import type { SynthesisVersion } from '@mss_creator/shared';
import { Alert } from '@pms-digital/design-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { digestFailureMessage } from './digest-messages';
import { DigestVersionCard } from './digest-version-card';

/**
 * The cross-document synthesis (level 2 of D9) — derived from the per-file
 * digests, never from the raw text, which is what lets it reconcile overlap
 * and surface contradictions between sources.
 *
 * Rendered only when at least two files have digests: with a single file that
 * file's digest *is* the context (D11), and the backend answers 409 for both
 * routes here in that case.
 */

export interface SynthesisDigestCardProps {
  matrixId: string;
  /** Null until the synthesis has been produced once. */
  synthesis: SynthesisVersion | null;
  /** `matrixDigestState.synthesisCurrent` — false means the digests moved under it (D13). */
  current: boolean;
  disabled: boolean;
  /** The matrix has no sujet/contexte — the model-calling button alone is blocked (issue #1). */
  framingMissing?: boolean;
  /** `matrixDigestState.synthesisStale` — one of its sources predates the current framing (issue #1). */
  stale?: boolean;
  /** Called before any model call, so the wizard's autosave debounce lands first. */
  onBeforeModelCall?: () => void;
}

export function SynthesisDigestCard({
  matrixId,
  synthesis,
  current,
  disabled,
  framingMissing = false,
  stale: framingStale = false,
  onBeforeModelCall,
}: SynthesisDigestCardProps) {
  const queryClient = useQueryClient();

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['digest-state', matrixId] });
    void queryClient.invalidateQueries({ queryKey: ['matrix', matrixId] });
  }

  const regenerateMutation = useMutation({
    mutationFn: (comment: string | null) => {
      onBeforeModelCall?.();
      return digestsApi.regenerateSynthesis(matrixId, comment === null ? {} : { comment });
    },
    onSuccess: invalidate,
  });

  const editMutation = useMutation({
    mutationFn: (content: string) => digestsApi.editSynthesis(matrixId, { content }),
    onSuccess: invalidate,
  });

  const failedMutation = regenerateMutation.isError
    ? regenerateMutation.error
    : editMutation.isError
      ? editMutation.error
      : null;

  /**
   * A stale synthesis is the one state here that must not be quiet: it is what
   * actually reaches the generation prompt (D10), so a synthesis written
   * against digests that have since changed silently shapes every cell.
   * "No synthesis yet" is not staleness — there is nothing to be stale — so
   * the banner is tied to an existing version.
   */
  const stale = synthesis !== null && !current;
  // Same conclusion, different cause: the digests moved (D13) or the framing
  // did (issue #1). Both mean the text that reaches the prompt was built
  // against inputs the matrix no longer has, so they share one banner slot —
  // with the digest-set cause taking the wording when both apply, since that
  // is the one a regeneration here actually fixes.
  const showStale = stale || (synthesis !== null && framingStale);

  return (
    <DigestVersionCard
      title="Synthèse des documents"
      subject="la synthèse"
      version={synthesis}
      badge={
        showStale ? (
          <span className="rounded-md border border-warning bg-warning-tint px-2 py-0 font-semibold text-caption">
            À refaire
          </span>
        ) : undefined
      }
      notice={
        showStale ? (
          <Alert tone="warning">
            {stale
              ? 'Cette synthèse a été produite à partir de digests qui ont changé depuis. C’est elle qui est envoyée au modèle : régénérez-la avant de lancer la génération.'
              : 'Une des sources de cette synthèse a été produite sous le cadrage précédent. Refaites-la une fois les digests concernés redéroulés.'}
          </Alert>
        ) : undefined
      }
      emptyLabel="La synthèse n’a pas encore été produite — elle réconcilie les documents entre eux et signale leurs contradictions."
      editFieldLabel="Texte de la synthèse"
      disabled={disabled}
      regenerateDisabled={framingMissing}
      regeneratePending={regenerateMutation.isPending}
      editPending={editMutation.isPending}
      error={failedMutation ? digestFailureMessage(failedMutation) : null}
      onRegenerate={(comment) => regenerateMutation.mutateAsync(comment)}
      onEdit={(content) => editMutation.mutateAsync(content)}
    />
  );
}
